/**
 * Builds the customer card's document tree and narrows the file list to the
 * branch that is selected.
 *
 * The files themselves are already loaded by the tab (useCustomerFiles); the
 * only extra request is the company's flat category list, which supplies the
 * titles of parent categories that hold no files of their own. It is cached
 * per company: the list changes far more rarely than the tab is opened.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import useRestorableState from "hooks/useRestorableState";
import { fetchAllFileCategories } from "services/files-category.service";
import {
  ALL_NODE_ID,
  DocumentTree,
  FlatCategory,
  TreeFile,
  UNCATEGORIZED_NODE_ID,
  buildDocumentTree,
  findNode,
} from "./build-document-tree";

const categoryCache = new Map<string, FlatCategory[]>();

interface Options {
  companyId?: string;
  files: TreeFile[];
}

interface Result {
  tree: DocumentTree;
  selectedId: string;
  selectedTitle: string;
  selectCategory: (id: string) => void;
  visibleFiles: TreeFile[];
}

export default function useCustomerDocumentTree({
  companyId,
  files,
}: Options): Result {
  const [categories, setCategories] = useState<FlatCategory[]>(() =>
    companyId ? categoryCache.get(companyId) || [] : []
  );

  useEffect(() => {
    if (!companyId) return undefined;
    const cached = categoryCache.get(companyId);
    if (cached) {
      setCategories(cached);
      return undefined;
    }
    let isCurrent = true;
    fetchAllFileCategories(companyId)
      .then((response: FlatCategory[]) => {
        const list = Array.isArray(response) ? response : [];
        categoryCache.set(companyId, list);
        if (isCurrent) setCategories(list);
      })
      .catch(() => {
        // The tree still builds from the categories the files carry; only the
        // titles of file-less parent categories are missing.
        if (isCurrent) setCategories([]);
      });
    return () => {
      isCurrent = false;
    };
  }, [companyId]);

  const tree = useMemo(
    () => buildDocumentTree(files, categories),
    [files, categories]
  );

  const [storedId, setStoredId] = useRestorableState<string>(
    "customer-documents-category",
    ALL_NODE_ID
  );

  // A stored selection can point at a category that this customer no longer
  // has files in (another customer, a narrower search) — fall back to showing
  // everything instead of an empty list, without forgetting the selection.
  const selectedNode = useMemo(
    () =>
      storedId && storedId !== UNCATEGORIZED_NODE_ID
        ? findNode(tree.nodes, storedId)
        : null,
    [tree, storedId]
  );

  let selectedId = ALL_NODE_ID;
  if (storedId === UNCATEGORIZED_NODE_ID) {
    selectedId = tree.uncategorizedFileIds.length
      ? UNCATEGORIZED_NODE_ID
      : ALL_NODE_ID;
  } else if (selectedNode) {
    selectedId = storedId;
  }

  const selectCategory = useCallback(
    (id: string) => {
      setStoredId(id);
    },
    [setStoredId]
  );

  const visibleFiles = useMemo(() => {
    if (selectedId === ALL_NODE_ID) return files;
    const ids = new Set(
      selectedId === UNCATEGORIZED_NODE_ID
        ? tree.uncategorizedFileIds
        : selectedNode?.fileIds || []
    );
    return files.filter((file) => ids.has(file.id));
  }, [files, selectedId, selectedNode, tree]);

  return {
    tree,
    selectedId,
    selectedTitle: selectedNode?.title || "",
    selectCategory,
    visibleFiles,
  };
}
